import type { QueryFilter } from '@mariana/labos-client';
import type { EntityType } from '../types';
import type { ColumnDef } from '../config/viewsConfig';
import { useLocalEntityList } from './useLocalEntityList';
import type { UseEntityListResult } from './useEntityList';
import { usePersistedColumns } from './usePersistedColumns';

interface UseLocalViewStateOptions {
  entityType: EntityType;
  allColumns: ColumnDef[];
  initialFilters?: QueryFilter[];
  defaultSort?: { by: string; order: 'asc' | 'desc' };
  extraFilters?: QueryFilter[];
  search?: string;
  initialLimit?: number;
  /** Column selections are persisted to localStorage under this key. */
  storageKey: string;
}

export interface UseLocalViewStateResult extends UseEntityListResult {
  visibleColumns: ColumnDef[];
  setVisibleColumns: (cols: ColumnDef[]) => void;
  allColumns: ColumnDef[];
}

export function useLocalViewState({
  entityType,
  allColumns,
  initialFilters = [],
  defaultSort,
  extraFilters,
  search,
  initialLimit,
  storageKey,
}: UseLocalViewStateOptions): UseLocalViewStateResult {
  const { columns: visibleColumns, setColumns: handleSetVisibleColumns } = usePersistedColumns(
    storageKey, allColumns, entityType,
  );

  const entityList = useLocalEntityList({
    entityType,
    columns: allColumns,
    initialFilters,
    defaultSort,
    search,
    extraFilters,
    initialLimit,
  });

  return {
    ...entityList,
    visibleColumns,
    setVisibleColumns: handleSetVisibleColumns,
    allColumns,
  };
}
